import type { InnspelClient, MySubmission } from '@innspel/core';

/**
 * H3.1 (concept page v0.1 2026-10-29, screen 3) — offline, «Mine innspill» shows the list as
 * it was last fetched THIS SESSION, with the time, instead of only the queue.
 *
 * In memory, never in storage: «the core stores no list» is a rejected alternative in DD-62,
 * and the queue is the only thing on disk (DD-41). A reload starts empty, and that is the
 * price of not keeping the tenant's replies on the machine.
 *
 * The core answers a network failure with the queue alone (DD-41), so a result without server
 * rows is ambiguous: «no cases» or «the server did not answer». The browser knows the
 * network (`navigator.onLine`, an offer, not a truth): known online, the result is the truth
 * and is remembered even when empty (a deletion per pseudonym, K11, must not resurface);
 * known offline with no server rows, the remembered list is shown with its time.
 */
export interface SistKjent {
  at: string;
  rader: MySubmission[];
}

export interface MineResultat {
  rader: MySubmission[];
  /** Set when `rader` is the remembered list — the component then shows the time and no promise. */
  sistKjentAt?: string;
}

export async function hentMine(client: InnspelClient, minne: { current: SistKjent | undefined }, offline: boolean): Promise<MineResultat> {
  const rader = await client.subjects.submissions();
  const server = rader.filter((r) => r.pending !== true);
  if (server.length > 0 || !offline) {
    minne.current = { at: new Date().toISOString(), rader: server };
    return { rader };
  }
  if (minne.current) return { rader: [...rader, ...minne.current.rader], sistKjentAt: minne.current.at };
  return { rader };
}

/** Swaps the updated case into the remembered list too, so an addendum survives going offline. */
export function oppdaterSistKjent(minne: { current: SistKjent | undefined }, sak: MySubmission & { pending?: false }): void {
  if (!minne.current) return;
  minne.current = { ...minne.current, rader: minne.current.rader.map((r) => (r.pending !== true && r.id === sak.id ? sak : r)) };
}

/** `navigator.onLine === false` right now; `false` where there is no navigator (SSR, tests without one). */
export function erOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}
